import { useQuery } from "@apollo/client/react";
import { Card, Group, Stack, Text } from "@mantine/core";
import { graphql } from "gql.tada";
import { useParams } from "react-router-dom";
import { Scanner } from "@yudiel/react-qr-scanner";
import { decryptData } from "../../utils/encryption.helpers";
import { modals } from "@mantine/modals";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { PageHeader } from "../../components/PageHeader";
import { DetailPageSkeleton } from "../../components/SkeletonLoader";

const EVENT_DETAILS = graphql(`
  query eventDetails($id: ID!) {
    event(id: $id) {
      title
      decryptionKey
    }
  }
`);

export const EventQRScanner = () => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const eventId = useParams().eventId!;
  const { data, loading, error } = useQuery<any>(EVENT_DETAILS, {
    variables: { id: eventId },
  });

  if (loading) {
    return (
      <div className="wp-fade-in">
        <PageHeader title="QR Scanner" showBackButton showBreadcrumbs={false} />
        <DetailPageSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="wp-fade-in">
        <PageHeader title="QR Scanner" showBackButton showBreadcrumbs={false} />
        <Card p="xl" withBorder>
          <Group gap="xs">
            <IconAlertCircle color="red" size={20} />
            <Text c="red" fw={500}>
              Failed to load event: {error.message}
            </Text>
          </Group>
        </Card>
      </div>
    );
  }

  if (!data?.event) {
    return (
      <div className="wp-fade-in">
        <PageHeader title="QR Scanner" showBackButton showBreadcrumbs={false} />
        <Card p="xl" withBorder>
          <Group gap="xs">
            <IconAlertCircle color="red" size={20} />
            <Text c="red" fw={500}>
              Event not found
            </Text>
          </Group>
        </Card>
      </div>
    );
  }

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="QR Scanner"
        subtitle={`Scanning tickets for ${data.event.title as string}`}
        showBackButton
        showBreadcrumbs={false}
      />
      <Card p="md" withBorder style={{ minHeight: "calc(100vh - 160px)" }}>
        <Scanner
          onError={() => {
            notifications.show({
              id: "qr-scanner-error",
              title: "Error",
              message: "Error scanning QR code",
              color: "red",
            });
          }}
          onScan={async (result) => {
            try {
              const decryptedData = await decryptData<{
                ticketId: string;
                eventId: string;
                username: string;
              }>(result[0].rawValue, data.event.decryptionKey);

              if (decryptedData.eventId !== eventId) {
                modals.open({
                  id: "invalid-ticket",
                  title: "Invalid ticket",
                  children: (
                    <Group gap="xs">
                      <IconAlertCircle color="red" />
                      <Text c="red">
                        Ticket has a valid decryption key, but it is not
                        registered for this event. This should not happen.
                      </Text>
                    </Group>
                  ),
                });
                return;
              }

              modals.open({
                id: "valid-ticket",
                title: "Ticket valid for event",
                children: (
                  <Stack gap={0}>
                    <Group gap="xs">
                      <IconCheck color="green" />
                      <Text c="green">
                        Ticket is valid for event {data.event.title as string}
                      </Text>
                    </Group>
                    <Text>
                      <Text span fw={700}>
                        Username:
                      </Text>{" "}
                      {decryptedData.username}
                    </Text>
                    <Text>
                      <Text span fw={700}>
                        Ticket ID:
                      </Text>{" "}
                      {decryptedData.ticketId}
                    </Text>
                    <Text>
                      <Text span fw={700}>
                        Event ID:
                      </Text>{" "}
                      {decryptedData.eventId}
                    </Text>
                  </Stack>
                ),
              });
            } catch {
              modals.open({
                id: "invalid-ticket",
                title: "Invalid ticket",
                children: (
                  <Group gap="xs">
                    <IconAlertCircle color="red" />
                    <Text c="red">Ticket is not valid for this event</Text>
                  </Group>
                ),
              });
            }
          }}
        />
      </Card>
    </div>
  );
};
